import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock } from "lucide-react";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer data-testid="footer" className="bg-[#1A2C22] text-white">
      {/* Main Footer */}
      <div className="container mx-auto max-w-7xl px-4 md:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div>
            <h3 className="font-heading text-2xl font-bold mb-6">CasaCurate</h3>
            <p className="text-gray-300 text-sm leading-relaxed mb-6">
              Professional landscaping and gardening services for residential homeowners in Los Angeles. Creating beautiful outdoor spaces since 2015.
            </p>
            <div className="space-y-3">
              <a
                href="tel:+17711871113"
                data-testid="footer-phone"
                className="flex items-center gap-3 text-gray-300 hover:text-white transition-colors"
              >
                <Phone className="w-4 h-4 text-[#C2410C]" />
                <span>+1-771-1871-113</span>
              </a>
              <a
                href="mailto:info@casacurate.store"
                data-testid="footer-email"
                className="flex items-center gap-3 text-gray-300 hover:text-white transition-colors"
              >
                <Mail className="w-4 h-4 text-[#C2410C]" />
                <span>info@casacurate.store</span>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-lg mb-6">Quick Links</h4>
            <nav className="space-y-3">
              <Link
                to="/"
                data-testid="footer-link-home"
                className="block text-gray-300 hover:text-white transition-colors"
              >
                Home
              </Link>
              <Link
                to="/services"
                data-testid="footer-link-services"
                className="block text-gray-300 hover:text-white transition-colors"
              >
                Services
              </Link>
              <Link
                to="/projects"
                data-testid="footer-link-projects"
                className="block text-gray-300 hover:text-white transition-colors"
              >
                Projects
              </Link>
              <Link
                to="/about"
                data-testid="footer-link-about"
                className="block text-gray-300 hover:text-white transition-colors"
              >
                About Us
              </Link>
              <Link
                to="/contact"
                data-testid="footer-link-contact"
                className="block text-gray-300 hover:text-white transition-colors"
              >
                Contact
              </Link>
            </nav>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-semibold text-lg mb-6">Our Services</h4>
            <nav className="space-y-3">
              <Link
                to="/services"
                data-testid="footer-service-landscape"
                className="block text-gray-300 hover:text-white transition-colors"
              >
                Landscape Design
              </Link>
              <Link
                to="/services"
                data-testid="footer-service-lawn"
                className="block text-gray-300 hover:text-white transition-colors"
              >
                Lawn Installation
              </Link>
              <Link
                to="/services"
                data-testid="footer-service-maintenance"
                className="block text-gray-300 hover:text-white transition-colors"
              >
                Garden Maintenance
              </Link>
              <Link
                to="/services"
                data-testid="footer-service-irrigation"
                className="block text-gray-300 hover:text-white transition-colors"
              >
                Irrigation Setup
              </Link>
              <Link
                to="/services"
                data-testid="footer-service-mulching"
                className="block text-gray-300 hover:text-white transition-colors"
              >
                Mulching & Soil Prep
              </Link>
            </nav>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-semibold text-lg mb-6">Contact Info</h4>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-[#C2410C] mt-1 flex-shrink-0" />
                <div>
                  <p className="text-white font-medium">CasaCurate Store</p>
                  <p className="text-gray-300 text-sm">
                    1513 Sunset Boulevard<br />
                    Los Angeles, CA 90027
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Clock className="w-4 h-4 text-[#C2410C] mt-1 flex-shrink-0" />
                <div>
                  <p className="text-white font-medium">Business Hours</p>
                  <p className="text-gray-300 text-sm">
                    Mon - Fri: 7:00 AM - 6:00 PM<br />
                    Sat: 8:00 AM - 4:00 PM<br />
                    Sun: Closed
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Footer */}
      <div className="border-t border-white/10">
        <div className="container mx-auto max-w-7xl px-4 md:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-400 text-sm text-center md:text-left">
              &copy; {currentYear} CasaCurate Store. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <Link
                to="/privacy-policy"
                data-testid="footer-privacy"
                className="text-gray-400 hover:text-white text-sm transition-colors"
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms-conditions"
                data-testid="footer-terms"
                className="text-gray-400 hover:text-white text-sm transition-colors"
              >
                Terms & Conditions
              </Link>
              <Link
                to="/cancellation-policy"
                data-testid="footer-cancellation"
                className="text-gray-400 hover:text-white text-sm transition-colors"
              >
                Cancellation Policy
              </Link>
            </div>
          </div>
          <p className="text-gray-500 text-xs mt-4 text-center">
            Service pricing varies depending on property size, project scope, and material selection.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
